import React from "react";
import LeftVector from "../../assets/faq/left-Patterns.svg";
import RightVector from "../../assets/faq/right-element.svg";
import HighAttrition from "../../assets/hiring_quide/high_attrition.webp";
import LackOfStructure from "../../assets/hiring_quide/lack_of_structure.webp";
import LimitedTime from "../../assets/hiring_quide/limited_time.webp";
import RightTalentPool from "../../assets/hiring_quide/right_talent_pool.webp";
import SeasonalPeak from "../../assets/hiring_quide/seasonal_peak.webp";

const WhyMsmesStruggleToHireBlueCollar = () => {
    const blogPosts = [
        {
            title: "Limited Time for Recruitment",
            subtitle: "Most MSME owners are busy managing operations, sales, and finances. " +
                "Spending hours sorting resumes or interviewing candidates isn’t practical.",
            back_card_title: "Solution",
            back_card_subtitle: "Platforms like Kaigal pre-screen candidates and share only interested profiles, saving you time.",
            link: "#",
            image: LimitedTime
        },
        {
            title: "High Attrition Rates",
            subtitle: "Employees leaving within a few months creates constant disruption. Industries like sales," +
                " drivers, and telecalling see particularly high turnover.",
            back_card_title: "Solution",
            back_card_subtitle: "By matching candidates based on location, language, and interest," +
                " Kaigal helps improve retention. When employees feel the job is right for them, they stay longer.",
            link: "#",
            image: HighAttrition
        },
        {
            title: "Lack of Access to the Right Talent Pool",
            subtitle: "Traditional methods—like word of mouth or newspaper ads—don’t reach enough candidates." +
                " MSMEs often end up hiring whoever is available, not necessarily the best fit.",
            back_card_title: "Solution",
            back_card_subtitle: "Kaigal’s digital hiring platform connects businesses directly with thousands of " +
                "job seekers across states like Tamil Nadu, Karnataka, Andhra Pradesh, Telangana, and Assam.",
            link: "#",
            image: RightTalentPool
        },
        {
            title: "Seasonal or Peak-Time Hiring",
            subtitle: "During festival seasons or business expansions, the need for extra workers suddenly spikes." +
                " MSMEs struggle to hire quickly enough.",
            back_card_title: "Solution",
            back_card_subtitle: "With Kaigal, you can access a ready pool of workers and hire at scale during peak seasons.",
            link: "#",
            image: SeasonalPeak
        },
        {
            title: "Lack of Structured Hiring Process",
            subtitle: "Most small businesses don’t have an HR department, which leads to confusion in managing interviews," +
                " follow-ups, and joining.",
            back_card_title: "Solution",
            back_card_subtitle: "Kaigal acts as an extended HR arm for your business." +
                " From candidate sourcing to follow-ups, we make hiring simple and organized.",
            link: "#",
            image: LackOfStructure
        },
    ];

    return (
        <section className="relative bg-white overflow-hidden">
            <img
                src={LeftVector}
                alt="Left background"
                className="absolute top-0 left-0 w-80 md:w-49 lg:w-[300px] pointer-events-none opacity-50"
            />
            <img
                src={RightVector}
                alt="Right background"
                className="absolute bottom-0 right-0 w-80 md:w-96 lg:w-[300px] pointer-events-none opacity-50"
            />

            <div className="relative z-10 px-6 py-12 max-w-6xl mx-auto">
                {/* Header */}
                <div className="px-6 md:px-20 lg:px-24 pt-20 pb-6">
                    {/* Title (centered) */}
                    <h2
                        className="text-3xl md:text-5xl font-bold mb-4 text-center"
                        style={{ color: "#fa6011" }}
                    >
                        Why MSMEs Struggle to Hire Blue-Collar Workers (and How to Fix It in 2026)
                    </h2>
                </div>
                <div className="max-w-6xl mx-auto">
                    <p className="text-left text-lg md:text-xl text-gray-700 leading-relaxed font-semibold mb-4">
                        Running a business is never easy—especially when it comes to hiring the right people.
                        For most MSMEs (Micro, Small and Medium Enterprises), the biggest challenge isn’t
                        finding customers, but finding reliable employees who can keep the business running smoothly.
                    </p>
                    <p className="text-left text-lg md:text-xl text-gray-700 leading-relaxed font-semibold">
                        At Kaigal, we see this struggle every day from business owners across India.
                        Let’s break down the common reasons why hiring blue-collar and entry-level workers is difficult,
                        and how you can solve them in 2025.
                    </p>
                </div>
                <div className="flex flex-wrap justify-center gap-8 mb-16">
                    {blogPosts.map((post, index) => (
                        <div key={index} className="group [perspective:1000px] w-full sm:w-[48%] md:w-[30%] h-[400px] max-w-[400px]">
                            <div className="relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                                {/* Front side */}
                                <div className="absolute inset-0 rounded-[30px] bg-white shadow-lg p-6 flex flex-col items-start text-left [backface-visibility:hidden]">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-40 object-cover rounded-lg border mb-4"
                                    />
                                    <h3 className="text-lg font-semibold mb-2">{post.title}</h3>
                                    <p className="text-sm text-gray-600">{post.subtitle}</p>
                                </div>
                                {/* Back side */}
                                <div className="absolute inset-0 rounded-[30px] bg-gray-100 shadow-lg p-6 flex flex-col items-start justify-center text-left [transform:rotateY(180deg)] [backface-visibility:hidden]">
                                    <h3 className="text-lg font-semibold mb-2">{post.back_card_title}</h3>
                                    <p className="text-sm text-gray-600 mb-4">{post.back_card_subtitle}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="max-w-3xl mx-auto p-4">
                    <h2
                        className="text-4xl md:text-5xl font-bold mb-4 text-center"
                        style={{ color: "#fa6011" }}
                    >
                        How Kaigal Makes Hiring Easy for MSMEs
                    </h2>
                </div>
                <div className="max-w-6xl mx-auto mb-12">
                    <ul className="list-disc list-inside space-y-1 text-left">
                        {[
                            "Faster Hiring – Pre-screened profiles shared directly with you.",
                            "Regional Language Support – Making it easier to connect with workers.",
                            "Scalable Solutions – Hire one employee or a whole team.",
                        ].map((item) => (
                            <li
                                key={item}
                                className="text-lg md:text-xl text-gray-600 leading-relaxed font-bold cursor-pointer hover:text-[#008b93]"
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="bg-gray-100 rounded-lg py-12 px-6 text-center mt-12">
                    <h3 className="text-xl font-bold mb-4">
                        Hiring blue-collar and entry-level workers doesn’t have to be complicated.
                        With the right partner, MSMEs can focus on running their business instead of worrying about recruitment.
                    </h3>
                    <p className="text-lg mb-2 font-medium">
                        At Kaigal, we make it easy for you to hire sales executives,
                        drivers, telecallers, office admins, helpers, and accountants—quickly and reliably.
                    </p>
                    <p className="text-gray-600 mb-6">
                        If you’re an employer struggling with hiring, start with Kaigal today and see how simple recruitment can be.
                    </p>
                    <button className="px-6 py-3 bg-green text-white rounded-lg font-medium hover:bg-orange">
                        <a
                            href="https://recruit.kaigal.com/?utm_source=website&utm_medium=blog&utm_campaign=why_msme_struggle_to_hire_blue_collars#/signupcheck"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg font-bold text-xl hover:bg-orange-500"
                        >
                            Start Hiring with Kaigal
                        </a>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default WhyMsmesStruggleToHireBlueCollar;